import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { API_URL as BASE_API_URL } from "../../config/apiConfig";

// Use centralized API configuration
const API_URL = `${BASE_API_URL}/driver/auth`;

// Get driver from localStorage on initial load
const getDriverFromStorage = () => {
  try {
    const driver = localStorage.getItem('driver');
    const token = localStorage.getItem('driverToken');

    return {
      driver: driver ? JSON.parse(driver) : null,
      token: token || null
    };
  } catch (error) {
    console.error('Error loading driver from localStorage:', error);
    // Clear corrupted data
    localStorage.removeItem('driver');
    localStorage.removeItem('driverToken');
    return { driver: null, token: null };
  }
};

const { driver: storedDriver, token: storedToken } = getDriverFromStorage();

const initialState = {
  driver: storedDriver,
  token: storedToken,
  isError: false,
  isSuccess: false,
  isLoading: false,
  isPendingApproval: false,
  message: "",
};

// Register driver
export const registerDriver = createAsyncThunk(
  "driverAuth/register",
  async (originalFormData, thunkAPI) => {
    try {

      // Create a proper FormData object for file upload
      const uploadFormData = new FormData();

      // Extract and append basic form values
      const name = originalFormData.get("name");
      const email = originalFormData.get("email");
      const phone = originalFormData.get("phone");
      const password = originalFormData.get("password");
      const vehicleModel = originalFormData.get("vehicleModel");
      const vehicleNumber = originalFormData.get("vehicleNumber");
      const licenseNumber = originalFormData.get("licenseNumber");
      const licenseExpiry = originalFormData.get("licenseExpiry");
      const vehicleType = originalFormData.get("vehicleType");

      // Append basic fields
      uploadFormData.append("name", name);
      uploadFormData.append("email", email);
      uploadFormData.append("phone", phone);
      uploadFormData.append("password", password);
      uploadFormData.append("vehicleModel", vehicleModel);
      uploadFormData.append("vehicleNumber", vehicleNumber);
      uploadFormData.append("licenseNumber", licenseNumber);
      uploadFormData.append("licenseExpiry", licenseExpiry);
      uploadFormData.append("vehicleType", vehicleType);

      // Document types that can be uploaded
      const documentTypes = [
        "aadhaarCard",
        "driversLicense",
        "driverPhoto",
        "vehicleRC",
        "insuranceCertificate",
        "pucCertificate",
        "fitnessCertificate",
        "routePermit",
        "vehiclePhotoFront",
        "vehiclePhotoBack",
      ];

      // Add each document file if it exists
      let documentCount = 0;
      documentTypes.forEach((docType) => {
        const file = originalFormData.get(docType);
        if (file && file instanceof File) {
          uploadFormData.append(docType, file);
          documentCount++;
        }
      });



      // Make the API call
      const response = await axios.post(`${API_URL}/register`, uploadFormData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      return response.data;
    } catch (error) {
      console.error("❌ Registration failed with error:", error);
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();

      return thunkAPI.rejectWithValue(message);
    }
  },
);

// Login driver
export const loginDriver = createAsyncThunk(
  "driverAuth/login",
  async (driverData, thunkAPI) => {
    try {

      // Clear previous driver session before login
      localStorage.removeItem('driver');
      localStorage.removeItem('driverToken');

      const response = await axios.post(`${API_URL}/login`, driverData);

      // Check if the backend returned status: false (login failure)
      if (response.data && response.data.status === false) {
        // Clear localStorage on login failure
        localStorage.removeItem('driver');
        localStorage.removeItem('driverToken');
        // Reject the promise to trigger the rejected case
        return thunkAPI.rejectWithValue(response.data.message || 'Login failed');
      }

      if (response.data && response.data.status === true) {
        // Store driver data and token in localStorage for persistence
        if (response.data.data) {
          localStorage.setItem('driver', JSON.stringify(response.data.data));
        }
        if (response.data.token) {
          localStorage.setItem('driverToken', response.data.token);
        }
      }

      return response.data;
    } catch (error) {
      // Clear localStorage on login failure
      localStorage.removeItem('driver');
      localStorage.removeItem('driverToken');

      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();

      return thunkAPI.rejectWithValue(message);
    }
  },
);

// Logout driver
export const logoutDriver = createAsyncThunk("driverAuth/logout", async () => {
  // Clear localStorage
  localStorage.removeItem('driver');
  localStorage.removeItem('driverToken');
  return {};
});

export const driverAuthSlice = createSlice({
  name: "driverAuth",
  initialState,
  reducers: {
    reset: (state) => {
      state.isLoading = false;
      state.isError = false;
      state.isSuccess = false;
      state.isPendingApproval = false;
      state.message = "";
    },
    clearDriverAuth: (state) => {
      state.driver = null;
      state.token = null;
      state.isError = false;
      state.isSuccess = false;
      state.isLoading = false;
      state.isPendingApproval = false;
      state.message = "";
      // Clear localStorage
      localStorage.removeItem('driver');
      localStorage.removeItem('driverToken');
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(registerDriver.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(registerDriver.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        // Auto-login after successful registration if token is provided in data
        if (action.payload.data && action.payload.data.token) {
          state.driver = action.payload.data;
          state.token = action.payload.data.token;
          localStorage.setItem('driver', JSON.stringify(action.payload.data));
          localStorage.setItem('driverToken', action.payload.data.token);
        }
      })
      .addCase(registerDriver.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(loginDriver.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.isSuccess = false;
        state.isPendingApproval = false;
        state.message = "";
      })
      .addCase(loginDriver.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.isError = false;
        state.isPendingApproval = false;
        state.message = "";
        // Store the driver data properly - the payload contains { token, data }
        state.driver = action.payload.data || action.payload.driver || action.payload;
        // Also store the token if available
        if (action.payload.token) {
          state.token = action.payload.token;
        }
      })
      .addCase(loginDriver.rejected, (state, action) => {
        state.isLoading = false;
        state.isSuccess = false;

        // Check if this is a pending approval error (403)
        if (action.payload && action.payload.includes("pending approval")) {
          state.isPendingApproval = true;
          state.isError = false; // Not treating as an error
        } else {
          state.isError = true;
          state.isPendingApproval = false;
        }

        state.message = action.payload;
        state.driver = null;
        state.token = null;
      })
      .addCase(logoutDriver.fulfilled, (state) => {
        state.driver = null;
        state.token = null;
      });
  },
});

export const { reset, clearDriverAuth } = driverAuthSlice.actions;
export default driverAuthSlice.reducer;
